/**
 * MigrationService - Handles data migrations for the application
 * Runs once during app initialization to migrate data between versions
 */

import { UserSettings } from '../types/GameTypes';
import { SettingsRepository } from '../repositories/SettingsRepository';
import { StorageService } from './StorageService';
import { GameAnalyticsIntegration } from './GameAnalyticsIntegration';
import { DEFAULT_WORD_PACK } from '../data/wordpacks';
import { config } from '../config';

export class MigrationService {
  
  constructor(
    private settingsRepository: SettingsRepository,
    private storageService: StorageService,
    private analytics: GameAnalyticsIntegration
  ) {}

  /**
   * Entry point - handles all migrations synchronously
   * Call once during app initialization before other services initialize
   * Each migration is self-contained and checks its own conditions
   */
  async handleMigrations(): Promise<void> {
    try {
      await this.migrateWordLengthToWordPack();
      
      // Future migrations can be added here:
      // await this.migrateGameStateFormat();
      // await this.migrateAnalyticsData();
    } catch (error) {
      console.error('Migration failed, recovering:', error);
      await this.recoverFromMigrationFailure();
    }
  }

  /**
   * Migration: Convert wordLength setting to selectedWordPack
   * Self-contained - checks own conditions and exits early if not needed
   */
  private async migrateWordLengthToWordPack(): Promise<void> {
    // Check if this migration is needed
    const saved = this.storageService.get<any>(config.storageKeys.userSettings);
    if (!saved || saved.wordLength === undefined || saved.selectedWordPack !== undefined) {
      // Migration not needed - either no settings, no old wordLength, or already migrated
      return;
    }

    console.log('Starting wordLength -> selectedWordPack migration');
    
    // Keep backup in memory for recovery (not persisted)
    const settingsBackup = { ...saved };

    try {
      // Perform migration
      const migratedSettings = this.performWordLengthConversion(saved);
      
      // Save migrated settings
      this.storageService.set(config.storageKeys.userSettings, migratedSettings);
      
      console.log(`Migration completed: wordLength ${saved.wordLength} -> selectedWordPack ${migratedSettings.selectedWordPack}`);
      
      // Track successful migration
      this.analytics.trackSettingsChange(
        'selectedWordPack',
        `migrated_from_wordLength_${saved.wordLength}`,
        migratedSettings.selectedWordPack
      );
      
    } catch (error) {
      console.error('WordLength migration failed, restoring backup:', error);
      
      // Restore from memory backup
      this.storageService.set(config.storageKeys.userSettings, settingsBackup);
      
      // Track failed migration
      this.analytics.trackError(
        'wordLength_migration_failed',
        `Failed to migrate wordLength ${saved.wordLength}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'migration_recovery'
      );
      
      throw error; // Re-throw to trigger global recovery
    }
  }

  /**
   * Perform the actual wordLength to selectedWordPack conversion
   */
  private performWordLengthConversion(oldSettings: any): UserSettings {
    const migrated = { ...oldSettings };
    
    // Convert wordLength to selectedWordPack
    migrated.selectedWordPack = this.mapWordLengthToPack(oldSettings.wordLength);
    
    // Remove old property
    delete migrated.wordLength;
    
    return migrated;
  }

  /**
   * Map legacy wordLength values to equivalent pack IDs
   * (Extracted from original SettingsRepository logic)
   */
  private mapWordLengthToPack(wordLength: number): string {
    switch (wordLength) {
      case 5:
      case 6:
        return 'short-words';
      case 7:
      case 8:
        return 'medium-words';
      case 9:
      case 10:
        return 'long-words';
      default:
        return DEFAULT_WORD_PACK; // 'all-words'
    }
  }

  /**
   * Global recovery mechanism for migration failures
   * Called when individual migration recovery also fails
   * Last resort: delete corrupted settings to force fresh start with defaults
   */
  private async recoverFromMigrationFailure(): Promise<void> {
    console.log('Global migration recovery: removing all corrupted settings');
    
    try {
      this.storageService.remove(config.storageKeys.userSettings);
      
      // Track recovery action
      this.analytics.trackSettingsChange(
        'global_migration_recovery',
        'corrupted_settings',
        'cleared_for_defaults'
      );
      
      console.log('All settings cleared - app will use defaults');
    } catch (error) {
      console.error('Global recovery failed:', error);
      // At this point, we can't recover - app will need to handle missing settings
    }
  }
}